import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import isEqual from 'lodash/isEqual';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { InfoAlert } from '../InfoAlertField/InfoAlertField';
import { InputGroupCheckboxCardFieldsFirstUiOptions } from './schema';

export { InputGroupCheckboxCardFieldsFirstSchema } from './schema';

type FieldStatus = 'default' | 'success' | 'error';

interface PropertyEntry {
  title?: string;
  description?: string;
  type?: string;
  format?: string;
  pattern?: string;
  errorMessage?: string;
  const?: string;
  default?: string;
}

export const InputGroupCheckboxCardFieldsFirst = (
  props: FieldExtensionComponentProps<
    object,
    InputGroupCheckboxCardFieldsFirstUiOptions
  >,
) => {
  const { onChange, formData, uiSchema, schema, disabled, readonly, required } =
    props;

  const {
    title,
    description,
    properties = {},
  } = schema as {
    title?: string;
    description?: string;
    properties?: Record<string, PropertyEntry>;
  };

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  const uiOptions =
    (uiSchema?.['ui:options'] as
      | InputGroupCheckboxCardFieldsFirstUiOptions
      | undefined) ?? {};
  const infoText = uiOptions.infoText;
  const checkboxLabel = uiOptions.checkboxLabel ?? 'Replace value';
  const secretField = uiOptions.secretField ?? '';
  const isSecret = uiOptions.isSecret ?? false;

  const visibleKeys = useMemo(
    () =>
      Object.keys(properties).filter(
        key =>
          key !== secretField &&
          (properties[key] as PropertyEntry)?.const === undefined,
      ),
    [properties, secretField],
  );

  const [propertyValues, setPropertyValues] = useState<Record<string, string>>(
    () => (formData as Record<string, string>) ?? {},
  );
  const [fieldStatuses, setFieldStatuses] = useState<
    Record<string, FieldStatus>
  >({});
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});
  const [isChecked, setIsChecked] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const onChangeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmittedRef = useRef<any>(formData ?? null);

  useEffect(() => {
    return () => {
      if (onChangeTimerRef.current) {
        clearTimeout(onChangeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isEqual(lastEmittedRef.current, propertyValues)) {
      if (onChangeTimerRef.current) {
        clearTimeout(onChangeTimerRef.current);
      }

      onChangeTimerRef.current = setTimeout(() => {
        lastEmittedRef.current = propertyValues;
        onChangeRef.current?.(propertyValues);
      }, 0);
    }
  }, [propertyValues]);

  function setFieldState(key: string, status: FieldStatus, text: string) {
    setFieldStatuses(prev => ({ ...prev, [key]: status }));
    setFieldHelpers(prev => ({ ...prev, [key]: text }));
  }

  function validateProperty(key: string, value: string) {
    const property = properties[key] as PropertyEntry;
    const defaultHelper = property?.description ?? '';
    const isSecretProperty = key === secretField;
    const isRequired = isSecretProperty ? required && isChecked : required;

    if (isRequired && value.trim().length === 0) {
      setFieldState(
        key,
        'error',
        property?.errorMessage ?? 'This is a required field.',
      );
      return;
    }

    if (
      property?.pattern &&
      value.trim().length > 0 &&
      !new RegExp(property.pattern).test(value)
    ) {
      setFieldState(key, 'error', property?.errorMessage ?? 'Invalid format.');
      return;
    }

    setFieldState(key, 'default', defaultHelper);
  }

  const handlePropertyChange =
    (key: string) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setPropertyValues(prev => ({ ...prev, [key]: value }));
      validateProperty(key, value);
    };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsChecked(checked);

    if (!checked) {
      setPropertyValues(prev => {
        const next = { ...prev };
        delete next[secretField];
        return next;
      });
      setFieldState(secretField, 'default', '');
      return;
    }

    validateProperty(secretField, propertyValues[secretField] ?? '');
  };

  const handleSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPropertyValues(prev => ({ ...prev, [secretField]: value }));
    validateProperty(secretField, value);
  };

  const secretProperty = properties[secretField] as PropertyEntry | undefined;
  const secretPlaceholder =
    (uiSchema as Record<string, any> | undefined)?.[secretField]?.[
      'ui:placeholder'
    ] ?? '';

  return (
    <Box sx={{ backgroundColor, padding: '1rem', borderRadius: '0.25rem' }}>
      {title && (
        <Box
          component="h2"
          sx={{ marginBottom: '0.5rem', marginTop: 0, fontSize: '1.125rem' }}
        >
          {title}
        </Box>
      )}

      {description && <Box sx={{ marginBottom: '0.5rem' }}>{description}</Box>}

      {(title || description) && (
        <Divider sx={{ marginBottom: '1rem', marginTop: '0.5rem' }} />
      )}

      {visibleKeys.map(key => {
        const property = properties[key] as PropertyEntry;
        const placeholder =
          (uiSchema as Record<string, any> | undefined)?.[key]?.[
            'ui:placeholder'
          ] ?? '';
        const status = fieldStatuses[key] ?? 'default';

        return (
          <Box key={key} sx={{ mb: '1rem' }}>
            <TextField
              label={property?.title ?? key}
              placeholder={placeholder}
              value={propertyValues[key] ?? ''}
              onChange={handlePropertyChange(key)}
              onBlur={() => validateProperty(key, propertyValues[key] ?? '')}
              disabled={disabled || readonly}
              required={required as any}
              type={property?.format === 'email' ? 'email' : 'text'}
              variant="outlined"
              fullWidth
              error={status === 'error'}
              helperText={fieldHelpers[key] ?? property?.description ?? ''}
            />
          </Box>
        );
      })}

      {infoText && <InfoAlert text={infoText} sx={{ mb: '1.5rem' }} />}

      {secretField && (
        <Box sx={{ mb: isChecked && secretProperty ? '1rem' : 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={handleCheckboxChange}
                disabled={disabled || readonly}
              />
            }
            label={checkboxLabel}
          />
        </Box>
      )}

      {secretField && isChecked && secretProperty && (
        <Box sx={{ mb: '1rem' }}>
          <TextField
            label={secretProperty.title ?? secretField}
            placeholder={secretPlaceholder}
            value={propertyValues[secretField] ?? ''}
            onChange={handleSecretChange}
            onBlur={() =>
              validateProperty(secretField, propertyValues[secretField] ?? '')
            }
            disabled={disabled || readonly}
            required={(required && isChecked) as any}
            type={isSecret && !showSecret ? 'password' : 'text'}
            variant="outlined"
            fullWidth
            error={fieldStatuses[secretField] === 'error'}
            helperText={
              fieldHelpers[secretField] ?? secretProperty.description ?? ''
            }
            InputProps={{
              endAdornment: isSecret ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle visibility"
                    onClick={() => setShowSecret(prev => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      )}
    </Box>
  );
};
